import React, {useEffect, useState} from 'react';
import styles from '../styles/InstanceDetailsModal.module.css';
import {CopyButton} from './InstanceDetailsModal';
import {formatAge} from './InstanceRow';
import {FreeVm, freeVmColumns} from './InstanceRow/freeVm';

/**
 * A free VM's details, in the look of a paid instance's (InstanceDetailsModal): the same overlay, title, tabs and
 * lists (Milos, 2026-11-02: "Just use a previous one that was for normal VMs"; "It can show a bit of things"). It shows
 * only what the free resources overview returns: no Volume, Logs or Actionlog tab, which read a paid instance by its id,
 * and never the internal flavor name.
 */
const TABS = ['details', 'flavor', 'image'] as const;
type Tab = typeof TABS[number];

/** Where the VM is reached: its DNS name, or why it has none. */
const DnsName: React.FC<{vm: FreeVm}> = ({vm}) => {
    if (vm.kind === 'postgres') return <>None: private, reachable only from your free VM</>;
    if (!vm.dnsName) return <>Not published yet</>;
    return (
        <span style={{display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0}}>
            <a href={`https://${vm.dnsName}`} target="_blank" rel="noopener noreferrer" style={{minWidth: 0}}>
                <code style={{fontFamily: 'monospace', fontSize: '0.9rem', wordBreak: 'break-all'}}>{vm.dnsName}</code>
            </a>
            <CopyButton text={vm.dnsName}/>
        </span>
    );
};

const FreeVmDetailsModal: React.FC<{vm: FreeVm | null; onClose: () => void}> = ({vm, onClose}) => {
    const [activeTab, setActiveTab] = useState<Tab>('details');
    // Each VM opens on its Details tab.
    useEffect(() => { setActiveTab('details'); }, [vm?.operationId]);
    if (!vm) return null;
    const columns = freeVmColumns(vm);

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                <div className={styles.modalHeader}>
                    <h2>Instance: {vm.name}</h2>
                    <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                        <button onClick={onClose} className={styles.closeButton} aria-label="Close">×</button>
                    </div>
                </div>

                <div className={styles.tabHeader}>
                    {TABS.map(tab => (
                        <button key={tab} className={activeTab === tab ? styles.activeTab : ''} onClick={() => setActiveTab(tab)}>
                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </button>
                    ))}
                </div>

                <div className={styles.tabContent}>
                    {activeTab === 'details' && (
                        <div className={styles.section}>
                            <h4>Instance Details</h4>
                            <ul>
                                {/* The one long value, and what this view is opened for: first, across the whole row. */}
                                <li style={{gridColumn: '1 / -1'}}><strong>DNS name:</strong> <DnsName vm={vm}/></li>
                                <li><strong>ID:</strong> {vm.operationId}</li>
                                <li><strong>Created At:</strong> {vm.createdAt ? new Date(vm.createdAt).toLocaleString() : 'N/A'}</li>
                                <li><strong>Age:</strong> {formatAge(vm.createdAt)}</li>
                                <li><strong>Uptime:</strong> {columns.uptime}</li>
                                <li><strong>Image Name:</strong> {columns.imageFull}</li>
                            </ul>
                        </div>
                    )}

                    {activeTab === 'flavor' && (
                        <div className={styles.section}>
                            <h4>Flavor Info</h4>
                            <ul>
                                <li><strong>vCPUs:</strong> {vm.size.vcpus}</li>
                                <li><strong>RAM:</strong> {vm.size.memoryMib / 1024} GiB</li>
                                <li><strong>Disk:</strong> {vm.size.diskGib} GiB</li>
                            </ul>
                        </div>
                    )}

                    {activeTab === 'image' && (
                        <div className={styles.section}>
                            <h4>Image Info</h4>
                            {vm.image ? (
                                <ul>
                                    <li><strong>Name:</strong> {vm.image}</li>
                                </ul>
                            ) : (
                                <p>Image details not available</p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FreeVmDetailsModal;
